import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { modulesAPI, lessonsAPI, testsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Button from '../components/UIKit/Button';
import ModuleCard from '../components/courses/ModuleCard';
import LessonSelector from '../components/courses/LessonSelector';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading } from '../redux/slices/professionsSlice';
import { fetchSkills, selectAllSkills, selectSkillsLoading } from '../redux/slices/skillsSlice';

const ModuleCreate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation(['modules', 'courses', 'common']);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Мемоізовані Redux селектори
  const professions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);
  const skills = useSelector(selectAllSkills);
  const skillsLoading = useSelector(selectSkillsLoading);

  // Мемоізовані значення для перевірки прав
  const hasPermission = useMemo(() => {
    return user && (user.userMeta?.roles?.includes('admin') || user.userMeta?.roles?.includes('teacher'));
  }, [user]);

  // Мемоізовані значення для завантаження даних
  const shouldFetchProfessions = useMemo(() => {
    return professions.length === 0 && !professionsLoading;
  }, [professions.length, professionsLoading]);

  const shouldFetchSkills = useMemo(() => {
    return skills.length === 0 && !skillsLoading;
  }, [skills.length, skillsLoading]);

  const [lessonSelectorOpen, setLessonSelectorOpen] = useState(false);
  const [module, setModule] = useState({
    title: '',
    description: '',
    content: '',
    videoUrl: '',
    isDraft: true,
    lessons: [],
  });
  const [isExpanded, setIsExpanded] = useState(true);
  const [localModuleFields, setLocalModuleFields] = useState({
    'module-0-title': '',
    'module-0-description': '',
    'module-0-content': '',
  });
  const [isDraft, setIsDraft] = useState(true);

  // Мемоізовані обробники для ModuleCard
  const handleToggleExpanded = useCallback((moduleIndex, expanded) => {
    setIsExpanded(expanded);
  }, []);

  const handleModuleFieldChange = useCallback((moduleIndex, e) => {
    const { name, value } = e.target;
    setLocalModuleFields(prev => ({
      ...prev,
      [`module-${moduleIndex}-${name}`]: value
    }));
    // Также обновляем состояние module для синхронизации
    setModule(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleAddLesson = useCallback((moduleIndex) => {
    const tempId = `temp-lesson-${Date.now()}-${Math.random()}`;
    setModule(prev => ({
      ...prev,
      lessons: [
        ...(prev.lessons || []),
        {
          _id: tempId, // Временный ID для React key
          title: '',
          content: '',
          duration: 15,
          type: 'text',
          contentType: 'standard',
          contentBlocks: [],
          resources: [],
          practiceExercises: [],
          tests: [],
        }
      ]
    }));
  }, []);

  const handleOpenLessonSelector = useCallback((moduleIndex) => {
    setLessonSelectorOpen(true);
  }, []);

  const handleSelectLessons = useCallback((selectedLessons) => {
    selectedLessons.forEach(lesson => {
      const isLessonAlreadyAdded = module.lessons?.some(
        existingLesson => existingLesson._id === lesson._id
      );

      if (!isLessonAlreadyAdded) {
        setModule(prev => ({
          ...prev,
          lessons: [...(prev.lessons || []), lesson]
        }));
      }
    });

    setLessonSelectorOpen(false);
    toast.success(t('moduleCreate.lessonsAdded', { count: selectedLessons.length }));
  }, [module.lessons, t]);

  const handleLessonChange = useCallback((moduleIndex, action, data, lessonIndex) => {
    setModule(prev => {
      const newModule = { ...prev };
      if (!newModule.lessons) newModule.lessons = [];

      switch (action) {
        case 'update':
          newModule.lessons[lessonIndex] = data;
          break;
        case 'add':
          newModule.lessons.push(data);
          break;
        case 'addArray':
          newModule.lessons.push(...data);
          break;
        case 'remove':
          newModule.lessons.splice(lessonIndex, 1);
          break;
        default:
          break;
      }
      return newModule;
    });
  }, []);

  const handleDraftToggle = useCallback((moduleIndex, isDraft) => {
    setIsDraft(isDraft);
    setModule(prev => ({
      ...prev,
      isDraft
    }));
  }, []);

  // Загружаем профессии и навыки при монтировании компонента
  useEffect(() => {
    if (shouldFetchProfessions) {
      dispatch(fetchProfessions({ all: true, isCommon: 1 }));
    }
    if (shouldFetchSkills) {
      dispatch(fetchSkills({ all: true, isCommon: 1 }));
    }
  }, [dispatch, shouldFetchProfessions, shouldFetchSkills]);

  // Проверка прав на создание модуля
  if (!hasPermission) {
    return (
      <Container sx={{ py: { xs: 3, sm: 4 } }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('moduleCreate.accessDenied')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {t('moduleCreate.noPermission')}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/modules')}>
            {t('moduleEdit.back')}
          </Button>
        </Box>
      </Container>
    );
  }

  // Обработка отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();

    const title = module.title || localModuleFields['module-0-title'];
    if (!title.trim()) {
      toast.error(t('moduleCreate.titleRequired'));
      return;
    }

    try {
      setLoading(true);

      // Подготавливаем данные модуля для отправки (без тестов, они теперь в уроках)
      const moduleData = {
        title,
        description: module.description || localModuleFields['module-0-description'] || '',
        content: module.content || localModuleFields['module-0-content'] || '',
        videoUrl: module.videoUrl || localModuleFields['module-0-videoUrl'] || '',
        isDraft: isDraft,
        lessons: module.lessons || [],
      };

      // Сначала создаем модуль
      const response = await modulesAPI.create(moduleData);
      const createdModule = response.data.module;
      const moduleId = createdModule._id;

      console.log(`✓ Модуль создан с ID: ${moduleId}`);

      // Затем создаем уроки модуля последовательно
      if (module.lessons && module.lessons.length > 0) {
        console.log(`📚 Создаем ${module.lessons.length} уроков для модуля...`);

        for (let lessonIndex = 0; lessonIndex < module.lessons.length; lessonIndex++) {
          const lesson = module.lessons[lessonIndex];

          console.log(`  📖 Урок ${lessonIndex + 1}/${module.lessons.length}: "${lesson.title}"`);

          try {
            // Создаем базовую структуру урока
            const lessonData = {
              title: lesson.title || '',
              content: lesson.content || '',
              duration: Number(lesson.duration) || 15,
              type: lesson.type || 'text',
              contentType: lesson.contentType || 'standard',
              contentBlocks: lesson.contentBlocks || [],
              resources: lesson.resources || [],
              practiceExercises: lesson.practiceExercises || [],
              professions: lesson.professions || [],
              skills: lesson.skills || [],
              image: lesson.image || '',
              videoUrl: lesson.videoUrl || '',
              isDraft: lesson.isDraft === undefined ? false : lesson.isDraft,
              moduleId: moduleId
            };

            // Убираем временный ID перед отправкой на сервер
            if (lessonData._id && lessonData._id.startsWith('temp-')) {
              delete lessonData._id;
            }

            let lessonId;
            const hasImageToUpload = lesson.imageFile || (lesson.imagePreview && !lesson.image);

            if (hasImageToUpload) {
              // Создаем урок с изображением
              const formData = new FormData();

              const lessonDataForUpload = {
                ...lessonData,
                imagePreview: undefined,
                imageFile: undefined
              };

              formData.append('data', JSON.stringify(lessonDataForUpload));

              // Добавляем изображение
              if (lesson.imageFile) {
                formData.append('image', lesson.imageFile);
              } else if (lesson.imagePreview) {
                const imageBlob = await fetch(lesson.imagePreview).then(r => r.blob());
                formData.append('image', imageBlob, 'lesson-image.jpg');
              }

              const lessonResponse = await lessonsAPI.createWithImage(formData);
              lessonId = lessonResponse.data.data?._id || lessonResponse.data._id || lessonResponse.data.lesson?._id;
            } else {
              // Создаем урок без изображения
              const lessonResponse = await lessonsAPI.create(lessonData);
              lessonId = lessonResponse.data.data?._id || lessonResponse.data._id || lessonResponse.data.lesson?._id;
            }

            if (!lessonId) {
              throw new Error('Не получен ID созданного урока');
            }

            console.log(`    ✓ Урок создан с ID: ${lessonId}`);

            // Обновляем состояние урока после успешного сохранения
            setModule(prev => ({
              ...prev,
              lessons: prev.lessons.map((l, idx) =>
                idx === lessonIndex ? { ...l, _id: lessonId, imagePreview: null, imageFile: null } : l
              )
            }));

            // Сохраняем тесты урока
            const lessonTests = lesson.tests || [];
            if (lessonTests.length > 0) {
              console.log(`    🧪 Создаем ${lessonTests.length} тестов для урока...`);

              for (let testIndex = 0; testIndex < lessonTests.length; testIndex++) {
                const test = lessonTests[testIndex];

                console.log(`      📝 Тест ${testIndex + 1}/${lessonTests.length}: "${test.title}"`);

                const testData = {
                  lessonId: lessonId,
                  title: test.title,
                  description: test.description || '',
                  timeLimit: Number(test.timeLimit) || 15,
                  passingScore: Number(test.passingScore) || 70,
                  attempts: Number(test.attempts) || 3,
                  questions: test.questions.map((q) => ({
                    ...q,
                    points: Number(q.points) || 1,
                    type: q.type || 'single-choice',
                  })),
                };

                // Убираем временный ID перед отправкой
                if (testData._id && testData._id.startsWith('temp-')) {
                  delete testData._id;
                }

                try {
                  if (test._id && !test._id.startsWith('temp-')) {
                    await testsAPI.update(test._id, testData);
                    console.log(`        ✓ Тест обновлен`);
                  } else {
                    await testsAPI.create(testData);
                    console.log(`        ✓ Тест создан`);
                  }
                } catch (error) {
                  console.error(`        ✗ Ошибка при создании теста "${test.title}":`, error);
                }
              }
            }
          } catch (error) {
            console.error(`    ✗ Ошибка при создании урока "${lesson.title}":`, error);
            toast.error(`Ошибка при создании урока "${lesson.title}"`);
          }
        }
      }

      toast.success(t('moduleCreate.createSuccess'));
      navigate(`/modules/${createdModule.slug || createdModule._id}`);
    } catch (error) {
      console.error('Ошибка при создании модуля:', error);
      toast.error(t('modulesPage.errors.loadError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container sx={{ py: { xs: 3, sm: 4 } }}>
      <Box sx={{ mb: 4 }}>
        {/* Заголовок */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/modules')}
            >
              {t('moduleEdit.back')}
            </Button>
            <Typography variant="h4" component="h1" fontWeight="bold">
              {t('moduleCreate.title')}
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            {t('moduleCreate.subtitle')}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* ModuleCard */}
            <Grid item xs={12}>
              <ModuleCard
                moduleRef={module}
                moduleIndex={0}
                totalModules={1}
                isExpanded={isExpanded}
                localModuleFields={localModuleFields}
                user={user}
                isDraft={isDraft}
                showOrderField={false}
                onToggleExpanded={handleToggleExpanded}
                onModuleFieldChange={handleModuleFieldChange}
                onRemoveModule={() => {}}
                onAddLesson={handleAddLesson}
                onOpenLessonSelector={handleOpenLessonSelector}
                onLessonChange={handleLessonChange}
                onModuleOrderChange={() => {}}
                onDraftToggle={handleDraftToggle}
              />
            </Grid>

            {/* Кнопки действий */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                  onClick={handleSubmit}
                  fullWidth
                >
                  {loading ? t('moduleCreate.saving') : t('moduleCreate.saveChanges')}
                </Button>

                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate('/modules')}
                  disabled={loading}
                  fullWidth
                >
                  {t('moduleCreate.cancel')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>

      {/* Диалог выбора уроков из библиотеки */}
      <LessonSelector
        open={lessonSelectorOpen}
        onClose={() => setLessonSelectorOpen(false)}
        onSelectLessons={handleSelectLessons}
      />
    </Container>
  );
};

export default ModuleCreate;
